import { useRef, useState } from 'react'
import type { JSX } from 'react'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import {
  Button,
  Flex,
  Form,
  toast,
  LightbulbIcon,
  RadioCard,
  Text,
  Textarea,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useUser } from '../../../../../common/context/User'
import { MODALS } from '../../../../../common/types'
import {
  canManageBilling,
  isOneBufferOrganization,
  isTeamUser,
} from '../../../../../common/utils/user'
import {
  DAYS_BEFORE_SHOWING_TWO_MONTH,
  isEligibleForAnnualUpgrade,
} from '../../../../../exports/Navigator/utils/shouldShowAnnualUpgradeBanner'
import { useModalManager } from '../../../../ModalManager/hooks/useModalManager'

import styles from '../index.module.css'
import { CancelFlowStepFrame } from './CancelFlowStepFrame'

const SUPPORT_URL = 'https://support.buffer.com/?openContact=true'
const SUPPORT_WINDOW_FEATURES = 'noopener,noreferrer'

const openSupport = (): boolean => {
  if (window.Brainfish?.Widgets?.open) {
    try {
      window.Brainfish.Widgets.open()
      return true
    } catch {
      // Fall back to the support URL below.
    }
  }

  try {
    return window.open(SUPPORT_URL, '_blank', SUPPORT_WINDOW_FEATURES) !== null
  } catch {
    return false
  }
}

type SurveyOptionFollowUp =
  | {
      kind: 'textarea'
      field: 'details' | 'otherDetails'
      label: string
      placeholder: string
    }
  | {
      kind: 'supportCta' | 'annualCta' | 'downgradeCta'
      buttonLabel: string
      buttonVariant: 'primary' | 'secondary'
      message: string
      onClick: () => void
    }

type SurveyOption = {
  value: string
  label: string
  followUp?: SurveyOptionFollowUp
}

const shuffleSurveyOptionValues = (values: string[]): string[] => {
  const shuffledValues = [...values]

  for (let index = shuffledValues.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1))
    const currentValue = shuffledValues[index]
    shuffledValues[index] = shuffledValues[randomIndex]
    shuffledValues[randomIndex] = currentValue
  }

  return shuffledValues
}

const useRandomizedSurveyOptions = (
  options: SurveyOption[],
): SurveyOption[] => {
  // Cache the randomized value order for this mounted survey. The option
  // objects are rebuilt each render so CTA callbacks stay current, but the
  // visual order does not jump while users select reasons or type feedback.
  const optionOrderCacheRef = useRef<Map<string, string[]>>(new Map())
  const optionValues = options.map((option) => option.value)
  const optionValuesKey = optionValues.join('|')
  let randomizedOptionValues = optionOrderCacheRef.current.get(optionValuesKey)

  if (!randomizedOptionValues) {
    randomizedOptionValues = shuffleSurveyOptionValues(optionValues)
    optionOrderCacheRef.current.set(optionValuesKey, randomizedOptionValues)
  }

  const optionOrder = new Map(
    randomizedOptionValues.map((value, index) => [value, index]),
  )

  return [...options].toSorted(
    (optionA, optionB) =>
      (optionOrder.get(optionA.value) ?? 0) -
      (optionOrder.get(optionB.value) ?? 0),
  )
}

function buildSurveyOptions({
  canShowDowngradePlan,
  canSwitchToAnnual,
  isTrialCancellation,
  onDowngradePlan,
  onOpenSupport,
  onSwitchToAnnual,
}: {
  canShowDowngradePlan: boolean
  canSwitchToAnnual: boolean
  isTrialCancellation: boolean
  onDowngradePlan: () => void
  onOpenSupport: () => void
  onSwitchToAnnual: () => void
}): SurveyOption[] {
  const tellUsMore: SurveyOptionFollowUp = {
    kind: 'textarea',
    field: 'details',
    label: 'Tell us more about why you are cancelling',
    placeholder: 'Tell us more (optional)',
  }

  const options: SurveyOption[] = [
    {
      value: 'not-using',
      label: "I'm not using Buffer enough",
      followUp: tellUsMore,
    },
    {
      value: 'too-expensive',
      label: isTrialCancellation
        ? 'Buffer will be too expensive after my trial'
        : 'Buffer is too expensive',
      followUp: tellUsMore,
    },
    {
      value: 'missing-features',
      label: 'There are missing features I need',
      followUp: {
        kind: 'textarea',
        field: 'details',
        label: 'What features are you missing?',
        placeholder: 'What features are you missing?',
      },
    },
    {
      value: 'posts-failing',
      label: 'My posts are failing too often',
      followUp: tellUsMore,
    },
    {
      value: 'technical-issues',
      label: 'I am having technical issues',
      followUp: {
        kind: 'supportCta',
        buttonLabel: 'Open Support',
        buttonVariant: 'secondary',
        message: 'Our support team can usually fix technical issues fast.',
        onClick: onOpenSupport,
      },
    },
    {
      value: 'seasonal',
      label: 'My business is seasonal',
      followUp: tellUsMore,
    },
  ]

  if (canSwitchToAnnual && !isTrialCancellation) {
    options.push({
      value: 'switch-annual',
      label: 'I want to change to a yearly subscription',
      followUp: {
        kind: 'annualCta',
        buttonLabel: 'Switch to Annual',
        buttonVariant: 'primary',
        message: 'Save up to 20% — switch to annual and get two months free.',
        onClick: onSwitchToAnnual,
      },
    })
  }

  if (canShowDowngradePlan && !isTrialCancellation) {
    options.push({
      value: 'downgrade-plan',
      label: 'I want to downgrade my plan',
      followUp: {
        kind: 'downgradeCta',
        buttonLabel: 'View Plans',
        buttonVariant: 'secondary',
        message: 'Switch to a smaller paid plan instead of cancelling.',
        onClick: onDowngradePlan,
      },
    })
  }

  return [
    ...options,
    {
      value: 'other',
      label: 'Other',
      followUp: {
        kind: 'textarea',
        field: 'otherDetails',
        label: 'Share any additional feedback',
        placeholder: 'Share any additional feedback...',
      },
    },
  ]
}

export type SurveyResponse = {
  reason: string
  details: string
  otherDetails: string
}

type SurveyStepProps = {
  initialResponse: SurveyResponse | null
  isLoading: boolean
  stepLabel?: string
  isTrialCancellation?: boolean
  onBack?: () => void
  onDismiss: () => void
  onContinue: (response: SurveyResponse) => void
}

type SurveyOptionPanelProps = {
  option: SurveyOption
  details: string
  otherDetails: string
  onDetailsChange: (value: string) => void
  onOtherDetailsChange: (value: string) => void
}

const SurveyOptionPanel = ({
  option,
  details,
  otherDetails,
  onDetailsChange,
  onOtherDetailsChange,
}: SurveyOptionPanelProps): JSX.Element | null => {
  if (!option.followUp) {
    return null
  }

  if (option.followUp.kind === 'textarea') {
    const value = option.followUp.field === 'details' ? details : otherDetails
    const onChange =
      option.followUp.field === 'details'
        ? onDetailsChange
        : onOtherDetailsChange

    return (
      <Form.Field
        name={option.followUp.field}
        className={styles.offerPanelTextarea}
      >
        <Form.Label>
          <VisuallyHidden>{option.followUp.label}</VisuallyHidden>
        </Form.Label>
        <Form.Control>
          <Textarea
            placeholder={option.followUp.placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </Form.Control>
      </Form.Field>
    )
  }

  return (
    <Flex
      align="center"
      gap="sm"
      justify="between"
      className={styles.inlineSuggestion}
    >
      <Text
        size="sm"
        color="subtle"
        icon={
          <LightbulbIcon
            size="xsmall"
            color="subtle"
            className={styles.suggestionIcon}
          />
        }
      >
        <Text as="span" size="sm" weight="medium">
          Suggestion:
        </Text>{' '}
        {option.followUp.message}
      </Text>
      <Button
        variant={option.followUp.buttonVariant}
        size="medium"
        onClick={option.followUp.onClick}
      >
        {option.followUp.buttonLabel}
      </Button>
    </Flex>
  )
}

export const SurveyStep = ({
  initialResponse,
  isLoading,
  stepLabel,
  isTrialCancellation = false,
  onBack,
  onDismiss,
  onContinue,
}: SurveyStepProps): JSX.Element => {
  const account = useUser()
  const { openModal } = useModalManager()
  const organizationId = account?.currentOrganization?.id ?? ''
  const commonTrackingProperties =
    account?.currentOrganization?.commonTrackingProperties ?? null
  const canShowDowngradePlan = account
    ? isTeamUser(account) &&
      canManageBilling(account) &&
      isOneBufferOrganization(account)
    : false
  const canSwitchToAnnual = account
    ? isEligibleForAnnualUpgrade(account, DAYS_BEFORE_SHOWING_TWO_MONTH)
    : false
  const [reason, setReason] = useState(initialResponse?.reason ?? '')
  const [details, setDetails] = useState(initialResponse?.details ?? '')
  const [otherDetails, setOtherDetails] = useState(
    initialResponse?.otherDetails ?? '',
  )
  const handleSwitchToAnnual = (): void => {
    if (organizationId) {
      BufferTracker.churnSurveyCTAClicked({
        organizationId,
        ctaName: 'cancelFlow-switch-to-annual',
        ...commonTrackingProperties,
      })
    }
    onDismiss()
    openModal({
      key: MODALS.annualUpgrade,
      data: {
        cta: 'annualUpgradeModal-cancelFlow',
        ctaButton: 'switchToAnnual',
        ctaView: 'annualUpgradeModal',
        ctaLocation: 'cancelFlow',
        ctaVersion: '1',
        upgradePathName: 'annualUpgrade',
      },
    })
  }

  const handleDowngradePlan = (): void => {
    if (organizationId) {
      BufferTracker.churnSurveyCTAClicked({
        organizationId,
        ctaName: 'cancelFlow-downgrade-plan',
        ...commonTrackingProperties,
      })
    }
    onDismiss()
    openModal({
      key: MODALS.planSelector,
      data: {
        cta: 'cancelFlow-downgrade',
        upgradePathName: 'cancelFlow-downgrade',
      },
    })
  }

  const handleOpenSupport = (): void => {
    if (organizationId) {
      BufferTracker.churnSurveyCTAClicked({
        organizationId,
        ctaName: 'cancelFlow-technical-issues-support',
        ...commonTrackingProperties,
      })
    }
    if (openSupport()) {
      onDismiss()
      return
    }

    toast.error(
      'Unable to open support. Please try again or contact us at support.buffer.com.',
    )
  }

  const baseOptions = buildSurveyOptions({
    canShowDowngradePlan,
    canSwitchToAnnual,
    onDowngradePlan: handleDowngradePlan,
    onOpenSupport: handleOpenSupport,
    onSwitchToAnnual: handleSwitchToAnnual,
    isTrialCancellation,
  })
  const options = useRandomizedSurveyOptions(baseOptions)
  const selectedOption = options.find((option) => option.value === reason)

  const handleReasonChange = (value: string): void => {
    if (organizationId) {
      BufferTracker.cancelFlowSurveyReasonSelected({
        organizationId,
        reason: value,
        ...commonTrackingProperties,
      })
    }
    setReason(value)
    setDetails('')
    setOtherDetails('')
  }

  const handleContinue = (): void => {
    const response: SurveyResponse = {
      reason,
      details: '',
      otherDetails: '',
    }
    const followUp = selectedOption?.followUp

    if (followUp?.kind === 'textarea') {
      response[followUp.field] =
        followUp.field === 'details' ? details : otherDetails
    }

    onContinue(response)
  }

  return (
    <CancelFlowStepFrame
      isLoading={isLoading}
      onBack={onBack}
      stepLabel={stepLabel}
      secondaryAction={{
        children: isTrialCancellation ? 'Keep My Trial' : 'Keep My Plan',
        onClick: onDismiss,
        variant: 'tertiary',
      }}
      primaryAction={{
        children: isTrialCancellation
          ? 'Continue Cancelling Trial'
          : 'Continue Cancellation',
        disabled: !reason,
        onClick: handleContinue,
        variant: 'critical',
      }}
      title="What’s the Main Reason You’re Leaving?"
      description="We use this information to improve our product."
      descriptionProps={{
        color: 'subtle',
        align: 'center',
        className: styles.channelsStepDescription,
      }}
      contentGap="md"
    >
      <RadioCard.Group
        aria-label="Cancellation reason"
        value={reason}
        onValueChange={handleReasonChange}
        className={styles.reasonGroup}
      >
        {options.map((option) => (
          <div key={option.value} className={styles.reasonItem}>
            <RadioCard
              value={option.value}
              className={
                reason === option.value && option.followUp
                  ? styles.reasonCardWithPanel
                  : undefined
              }
            >
              <Text>{option.label}</Text>
            </RadioCard>
            {reason === option.value && option.followUp && (
              <div className={styles.inlineSuggestionRow}>
                <SurveyOptionPanel
                  option={option}
                  details={details}
                  otherDetails={otherDetails}
                  onDetailsChange={setDetails}
                  onOtherDetailsChange={setOtherDetails}
                />
              </div>
            )}
          </div>
        ))}
      </RadioCard.Group>
    </CancelFlowStepFrame>
  )
}
